import {
    BookmarkBorderRounded,
    ContentCopyRounded,
    DeleteOutlineRounded,
    OpenWithRounded,
} from '@mui/icons-material';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Paper,
    Popper,
    Stack,
    TextField,
    Tooltip,
} from '@mui/material';
import { ReactEditor, useSlate } from 'slate-react';
import useSelectedElement from '@hooks/useSelectedElement';
import useEmailEditor from '@hooks/useEmailEditor';
import html2canvas from 'html2canvas';
import useEditorProps from '@hooks/useEditorProps';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { logCanvas } from '@Utils/dev';
import { getCanvasFile, htmlToImage, resizeCanvas } from '@Utils/htmlToImage';

function ElementControls({ element, path, anchorEl }) {
    const { t } = useTranslation();
    const editor = useSlate();
    const { removeElement, deselectElement } = useSelectedElement();
    const { setDraggedElement } = useEmailEditor();
    const { saveBlock } = useEditorProps();
    const [saveDialogOpen, setSaveDialogOpen] = useState(false);
    const [blockName, setBlockName] = useState('');

    const duplicateNode = () =>
        editor.insertNode(structuredClone(element), { at: path });

    const handleDragStart = (e: DragEvent) => {
        setDraggedElement(element.type);
        e.dataTransfer?.setDragImage(
            anchorEl,
            anchorEl.getBoundingClientRect().width + 12 + 20,
            12 + 20,
        );
        e.dataTransfer?.setData(
            'move-block/' + element.type,
            JSON.stringify(path),
        );
    };

    const saveNode = async () => {
        const nodeElement = ReactEditor.toDOMNode(editor, element);

        const thumbnail = await htmlToImage(nodeElement, { size: 200 });

        const block = {
            name: blockName,
            thumbnail,
            block: element,
        };
        saveBlock(block);

        setSaveDialogOpen(false);
        deselectElement();
    };

    return (
        <Popper
            disablePortal
            anchorEl={anchorEl}
            open={true}
            placement="right-start"
            modifiers={[
                {
                    name: 'preventOverflow',
                    enabled: true,
                    options: {
                        altAxis: true,
                        altBoundary: true,
                        tether: true,
                    },
                },
            ]}
            sx={{
                my: 8,
                pointerEvents: 'auto',
                zIndex: 1,
            }}
        >
            <Paper
                sx={{
                    borderRadius: 99,
                    marginX: 1.5,
                    // marginBlockStart: 1.5,
                    pointerEvents: 'auto',
                }}
                elevation={4}
            >
                <Stack>
                    <Tooltip title={t('Move')} placement="right">
                        <IconButton
                            size="small"
                            draggable
                            onDragStart={handleDragStart}
                            onDragEnd={() => setDraggedElement(null)}
                            onMouseDown={e => {
                                e.stopPropagation();
                            }}
                            color="primary"
                            sx={{
                                padding: 1,
                                borderBottomLeftRadius: 0,
                                borderBottomRightRadius: 0,
                                cursor: 'move',
                            }}
                        >
                            <OpenWithRounded />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={t('Copy')} placement="right">
                        <IconButton
                            size="small"
                            onMouseDown={duplicateNode}
                            sx={{
                                padding: 1,
                                borderRadius: 0,
                            }}
                        >
                            <ContentCopyRounded />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={t('Save')} placement="right">
                        <IconButton
                            size="small"
                            color="success"
                            onMouseDown={e => {
                                e.preventDefault();
                                e.stopPropagation();
                                setBlockName('');
                                setSaveDialogOpen(true);
                            }}
                            // onClick={removeNode}
                            sx={{
                                padding: 1,
                                borderRadius: 0,
                            }}
                        >
                            <BookmarkBorderRounded />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={t('Remove')} placement="right">
                        <IconButton
                            size="small"
                            onMouseDown={removeElement}
                            // onMouseDown={() => console.log('removeElement')}
                            // onClick={() => console.log('removeElement')}
                            color="error"
                            sx={{
                                padding: 1,
                                borderTopLeftRadius: 0,
                                borderTopRightRadius: 0,
                            }}
                        >
                            <DeleteOutlineRounded />
                        </IconButton>
                    </Tooltip>
                </Stack>
            </Paper>
            <Dialog
                open={saveDialogOpen}
                onClose={() => setSaveDialogOpen(false)}
                onMouseDown={e => e.stopPropagation()}
                PaperProps={{ sx: { borderRadius: 2 } }}
            >
                <DialogTitle
                    variant="subtitle1"
                    sx={{
                        padding: '8px 16px',
                    }}
                >
                    {t('Save Block')}
                </DialogTitle>
                <DialogContent
                    sx={{
                        paddingY: 0,
                        paddingX: 2,
                    }}
                >
                    <TextField
                        size="small"
                        autoFocus
                        label={t('Name')}
                        placeholder={t('Block Name')}
                        onChange={e => setBlockName(e.target.value)}
                        onKeyDown={e => {
                            if (e.key === 'Enter' && blockName) {
                                saveNode();
                            }
                        }}
                        sx={{ marginTop: 0.5 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button
                        size="small"
                        onClick={() => setSaveDialogOpen(false)}
                        sx={{ color: 'text.secondary' }}
                    >
                        {t('Cancel')}
                    </Button>
                    <Button
                        disabled={!blockName}
                        size="small"
                        onClick={saveNode}
                    >
                        {t('Save')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Popper>
    );
}

export default ElementControls;
